import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Overline } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { AudienceTag } from "../components/Tags";

const CATEGORIES = [
  {
    id: "language-services",
    n: "01",
    title: "Language Services",
    intro: "Human, professional language work — in the booth, at the table, on the page, and across the desk. English and Japanese, both directions.",
    audiences: ["business", "individual"],
    img: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Professionals in discussion around a table",
    action: ["Book a consultation", "/contact"],
    items: [
      ["Simultaneous Interpretation", "Real-time interpretation for conferences, summits, and live proceedings — faithful at the speed of speech.", ["Book a consultation", "/contact"]],
      ["Consecutive Interpretation", "Measured, precise interpretation for negotiations, site visits, and technical reviews.", ["Book a consultation", "/contact"]],
      ["On-Site Meeting & Negotiation Support", "A bilingual professional at your side in the room, reading context as well as words.", ["Book a consultation", "/contact"]],
      ["Executive & Board-Level Interpretation", "Discreet senior interpreters for governance, M&A, and investor relations.", ["Book a consultation", "/contact"]],
      ["Document Translation", "Contracts, reports, correspondence, and marketing materials — translated by professionals, EN⇄JP.", ["Contact us", "/contact"]],
      ["Technical Specification Translation", "Engineering documents, drawings, and manuals with terminology carried correctly.", ["Contact us", "/contact"]],
      ["1-to-1 Language Instruction", "Private English⇄Japanese instruction shaped around your work and your calendar.", ["Contact us", "/contact"]],
    ],
  },
  {
    id: "consulting-business-support",
    n: "02",
    title: "Consulting & Business Support",
    intro: "Strategy and steady operational support for organizations — and for the owners behind them — building something across the Pacific.",
    audiences: ["business", "individual"],
    img: "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Two professionals shaking hands across a meeting table",
    action: ["Book a consultation", "/contact"],
    items: [
      ["Market Entry Strategy", "Structure, partners, sequencing, and the first ninety days in Japan or Canada.", ["Book a consultation", "/contact"]],
      ["Business Introductions & Partnership Exploration", "Warm introductions and careful first meetings with vetted counterparts.", ["Book a consultation", "/contact"]],
      ["Cross-Cultural Strategy & Advisory", "Decisions informed by how business actually gets done on both sides of the Pacific.", ["Book a consultation", "/contact"]],
      ["Expatriate Integration", "Practical support for executives and families relocating between the two countries.", ["Contact us", "/contact"]],
      ["Organizational Culture Alignment", "Workshops and advisory that align merged or partner organizations around shared norms.", ["Book a consultation", "/contact"]],
      ["Operations Support", "Ongoing bilingual back-office support for owners, personal, and corporate clients.", ["Contact us", "/contact"]],
    ],
  },
  {
    id: "education-academics",
    n: "03",
    title: "Education & Academics",
    intro: "Programs that teach the language and the culture at once — from 1-on-1 lessons and ESL to executive sessions and institutional partnerships.",
    audiences: ["business", "individual"],
    img: "https://images.pexels.com/photos/1595391/pexels-photo-1595391.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Writing and study materials on a desk",
    action: ["Contact us", "/contact"],
    pageLink: ["View the full Education & Academics page", "/education-academics"],
    items: [
      ["1-on-1 Tutoring & Lessons", "Private lessons at your pace — conversation, exam preparation, or business fluency.", ["Contact us", "/contact"]],
      ["Language Courses & Programs", "Structured courses for individuals and teams, from beginner to professional fluency.", ["Contact us", "/contact"]],
      ["ESL Programs", "English-as-a-second-language programs for newcomers, employees, and their families.", ["Contact us", "/contact"]],
      ["Academic Institution Onboarding", "Getting enrolled, oriented, and set up at schools, colleges, and universities.", ["Contact us", "/contact"]],
      ["Support for Students Attending School Here", "Day-to-day guidance for students studying in Canada — and for the families sending them.", ["Contact us", "/contact"]],
      ["Seminars & Workshops", "Small-room sessions on cross-border business, communication, and culture — see Insights for dates.", ["View seminars", "/insights#seminars"]],
      ["Executive Private Sessions", "On-site leadership workshops for M&A, joint ventures, and executive relocation.", ["Book a consultation", "/contact"]],
      ["Program Development", "Custom curricula built with schools, companies, and institutions.", ["Contact us", "/contact"]],
      ["Institutional Partnerships", "Bridging universities, colleges, and schools across the two countries.", ["Book a consultation", "/contact"]],
      ["Academic Exchange Support", "Logistics, language, and liaison for exchange students and faculty.", ["Contact us", "/contact"]],
      ["Agent & Boarding Services", "Careful placement and guardianship support for students studying abroad.", ["Contact us", "/contact"]],
    ],
    extra: {
      title: "Executive Private Sessions — delivered on-site",
      body: "A senior practitioner comes to your leadership team and works through your real situation — confidentially, in English, Japanese, or both.",
      chips: ["Cross-Border M&A Workshops", "Joint Venture Alignment", "Executive Relocation Briefings"],
    },
  },
  {
    id: "relocation-navigation",
    n: "04",
    title: "Relocation & Navigation",
    intro: "The practical hundred steps of moving life or work between Japan and Canada — walked with you, in order.",
    audiences: ["individual"],
    img: "https://images.pexels.com/photos/1008155/pexels-photo-1008155.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Travellers walking through an airport terminal",
    action: ["Contact us", "/contact"],
    items: [
      ["Onboarding & Settlement Guidance", "Housing, banking, healthcare, and the first weeks on the ground.", ["Contact us", "/contact"]],
      ["Working Holiday Preparation & Support", "Preparation before departure and steady support after arrival.", ["Buy the guide", "/store"]],
      ["Travel Support", "Itineraries, bookings, and bilingual assistance while travelling.", ["Contact us", "/contact"]],
      ["Process Navigation", "Step-by-step guidance through Japanese and Canadian administrative systems.", ["Contact us", "/contact"]],
      ["Referral to Licensed Professionals", "When a matter needs a licensed professional, we introduce you to one we trust.", ["Contact us", "/contact"]],
    ],
    disclaimer: "Note: We do not provide immigration, visa, or settlement legal advice. These matters are referred to our trusted partner professionals.",
  },
  {
    id: "full-support",
    n: "05",
    title: "Full Support",
    intro: "For those who want us to manage the whole journey, not just one piece of it — coordinated across everything you need, at whatever level of involvement you choose.",
    audiences: ["business", "individual"],
    img: "https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "A warm, settled living space",
    action: ["Contact us", "/contact"],
    items: [
      ["Airport Arrival & First Days", "Pickup, initial orientation, getting settled.", ["Contact us", "/contact"]],
      ["Housing Search & Setup", "Finding a place, understanding leases, utilities.", ["Contact us", "/contact"]],
      ["Boarding Arrangements", "For students or those needing interim housing.", ["Contact us", "/contact"]],
      ["Ongoing Translation & Interpretation", "As things come up, not just scheduled sessions.", ["Contact us", "/contact"]],
      ["Coordinated Across Our Other Services", "Consulting, education, relocation — pulled together as one continuous relationship rather than separate bookings.", ["Book a consultation", "/contact"]],
    ],
  },
];

const AUDIENCE_OPTIONS = [
  { id: "individual", label: "Looking for individual support", sub: "Guides, courses, relocation, and personal support." },
  { id: "business", label: "Looking for business support", sub: "Interpretation, consulting, and team programs." },
  { id: "unsure", label: "Not sure yet", sub: "Show everything — I'll find my question." },
];

const RELEVANT = {
  individual: ["language-services", "education-academics", "relocation-navigation", "full-support"],
  business: ["language-services", "consulting-business-support", "education-academics", "full-support"],
};

export default function Services() {
  const [audience, setAudience] = useState(null);

  const isRelevant = (id) => !audience || audience === "unsure" || RELEVANT[audience].includes(id);

  const onSelect = (key) => {
    setAudience(key);
    if (key === "unsure") return;
    const first = RELEVANT[key][0];
    setTimeout(() => {
      document.getElementById(first)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  return (
    <div data-testid="services-page" className="bg-cream pt-16">
      <section className="bg-dawn">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pt-20 sm:pt-28 pb-10">
        <Reveal>
          <Overline>SERVICES</Overline>
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.12] tracking-tight text-navy max-w-3xl">
            Four practices. One promise: nothing gets lost between the languages.
          </h1>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-14">
            <p className="text-xs font-mono tracking-[0.2em] uppercase text-charcoal/60 mb-4">How can we help you?</p>
            <div data-testid="audience-selector" className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
              {AUDIENCE_OPTIONS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  data-testid={`audience-${o.id}`}
                  onClick={() => onSelect(o.id)}
                  className={`text-left p-5 rounded-sm border transition-all duration-300 ${
                    audience === o.id
                      ? "border-coral bg-coral-muted"
                      : "border-sand bg-cream-surface hover:border-navy/40"
                  }`}
                >
                  <span className={`block w-2.5 h-2.5 rounded-full border-2 mb-3 ${
                    audience === o.id ? "border-coral bg-coral" : "border-charcoal/30"
                  }`} />
                  <span className="font-serif text-lg text-navy">{o.label}</span>
                  <span className="block mt-1.5 text-xs leading-relaxed text-charcoal/60">{o.sub}</span>
                </button>
              ))}
            </div>
            {audience === "unsure" && (
              <p data-testid="audience-orienting-line" className="mt-5 text-sm text-charcoal/70 max-w-xl">
                Not sure yet — that's fine. Everything below is open to you; most engagements begin with a single conversation.
              </p>
            )}
          </div>
        </Reveal>
        </div>
      </section>

      {CATEGORIES.map((cat, ci) => (
        <section
          key={cat.id}
          id={cat.id}
          data-testid={`category-${cat.id}`}
          className={`border-t border-sand transition-opacity duration-500 ${isRelevant(cat.id) ? "" : "opacity-40"}`}
        >
          <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-16 sm:py-24">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 ${ci % 2 === 1 ? "" : ""}`}>
              <div className={ci % 2 === 1 ? "lg:order-2" : ""}>
                <Reveal>
                  <div className="border-t-2 border-navy pt-5">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-mono text-xs tracking-[0.22em] text-coral-dark uppercase">
                        {cat.n} — {cat.title}
                      </span>
                      {cat.audiences.map((a) => (
                        <AudienceTag key={a} audience={a} testId={`category-${cat.id}-tag-${a}`} />
                      ))}
                    </div>
                    <h2 className="mt-5 font-serif text-3xl sm:text-4xl tracking-tight text-navy">{cat.title}</h2>
                    <p className="mt-4 text-base leading-relaxed text-charcoal/70 max-w-lg">{cat.intro}</p>
                    <div className="mt-6 flex flex-wrap gap-3">
                      {cat.action && (
                        <Link
                          to={cat.action[1]}
                          data-testid={`category-${cat.id}-action`}
                          className="inline-flex items-center gap-1.5 text-xs font-mono tracking-wider uppercase border border-navy text-navy hover:bg-navy hover:text-cream-surface px-4 py-2 rounded-full transition-colors duration-300"
                        >
                          {cat.action[0]}
                        </Link>
                      )}
                      {cat.pageLink && (
                        <Link
                          to={cat.pageLink[1]}
                          data-testid={`category-${cat.id}-pagelink`}
                          className="inline-flex items-center gap-1.5 text-xs font-mono tracking-wider uppercase bg-coral hover:bg-coral-dark text-white px-4 py-2 rounded-full transition-colors duration-300"
                        >
                          {cat.pageLink[0]}
                        </Link>
                      )}
                    </div>
                  </div>
                </Reveal>
                <div className="mt-10">
                  {cat.items.map(([title, body, action], ii) => (
                    <Reveal key={title} delay={ii * 0.03}>
                      <div data-testid={`service-row-${cat.id}-${ii}`} className="border-t border-sand py-4 grid grid-cols-12 gap-3 items-baseline">
                        <span className="col-span-2 sm:col-span-1 font-mono text-[11px] text-coral-dark">
                          {cat.n}.{String(ii + 1).padStart(2, "0")}
                        </span>
                        <div className="col-span-10 sm:col-span-11">
                          <p className="text-sm font-medium text-navy">{title}</p>
                          <p className="mt-1 text-xs sm:text-sm leading-relaxed text-charcoal/60">{body}</p>
                          {action && (
                            <Link
                              to={action[1]}
                              data-testid={`service-row-action-${cat.id}-${ii}`}
                              className="group mt-2 inline-flex items-center gap-1 text-[11px] font-mono tracking-wider uppercase text-coral-dark hover:text-coral transition-colors duration-300"
                            >
                              {action[0]}
                              <ArrowRight size={11} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                            </Link>
                          )}
                        </div>
                      </div>
                    </Reveal>
                  ))}
                  {cat.disclaimer && (
                    <Reveal delay={0.1}>
                      <p data-testid="visa-disclaimer" className="mt-6 border-l-2 border-coral bg-coral-muted px-5 py-4 text-sm leading-relaxed text-charcoal/80">
                        {cat.disclaimer}
                      </p>
                    </Reveal>
                  )}
                  {cat.extra && (
                    <Reveal delay={0.1}>
                      <div data-testid={`category-${cat.id}-extra`} className="mt-8 bg-navy rounded-sm p-7">
                        <p className="font-serif text-xl text-cream-surface">{cat.extra.title}</p>
                        <p className="mt-3 text-sm leading-relaxed text-[#B7C6E2]">{cat.extra.body}</p>
                        <div className="mt-5 flex flex-wrap gap-2">
                          {cat.extra.chips.map((chip) => (
                            <span key={chip} className="text-[11px] font-mono tracking-wide border border-navy-border text-[#B7C6E2] px-3 py-1.5 rounded-full">
                              {chip}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Reveal>
                  )}
                </div>
              </div>
              <div className={ci % 2 === 1 ? "lg:order-1" : ""}>
                <Reveal delay={0.1} className="lg:sticky lg:top-24">
                  <div className="overflow-hidden rounded-sm border border-sand">
                    <img
                      src={cat.img}
                      alt={cat.alt}
                      data-testid={`category-${cat.id}-image`}
                      className="w-full h-72 lg:h-[30rem] object-cover transition-transform duration-700 hover:scale-[1.03]"
                      loading="lazy"
                    />
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </section>
      ))}

      <section className="border-t border-sand bg-cream-surface">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-16 sm:py-20">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
              <p className="font-serif text-2xl sm:text-3xl text-navy max-w-xl leading-snug">
                Working on something that doesn't fit these categories?{" "}
                <em className="text-coral-dark">That's usually where we're most useful.</em>
              </p>
              <div className="flex flex-wrap gap-4 shrink-0">
                <Link
                  to="/contact"
                  data-testid="services-catchall-contact"
                  className="group inline-flex items-center gap-2 bg-navy hover:bg-navy-surface text-cream-surface font-medium px-6 py-3 rounded-full transition-colors duration-300"
                >
                  Talk to us
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/services/index"
                  data-testid="services-az-index-link"
                  className="inline-flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-cream-surface font-medium px-6 py-3 rounded-full transition-colors duration-300"
                >
                  A–Z service index
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
